import React from 'react';
import { motion } from 'framer-motion';

const StrategySection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const strategies = [
    {
      title: 'Operational Excellence',
      description: 'Implementing institutional-grade property management and cost optimization strategies.',
      icon: (
        <svg className="w-7 h-7 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    },
    {
      title: 'Lease Modifications',
      description: 'Strategic lease restructuring to maximize NOI and property value.',
      icon: (
        <svg className="w-7 h-7 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      title: 'Asset Repositioning',
      description: 'Physical improvements and rebranding to capture higher market rents.',
      icon: (
        <svg className="w-7 h-7 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      )
    },
    {
      title: 'Market Timing',
      description: 'Disciplined acquisition during market dislocations and strategic exits at peak values.',
      icon: (
        <svg className="w-7 h-7 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      )
    }
  ];

  return (
    <section id="strategy" className="relative gradient-section-alt py-16 lg:py-24 overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-1/4 w-80 h-80 bg-[#D4AF37]/5 rounded-full blur-[100px]" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#1a2940]/50 rounded-full blur-[80px]" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
          {/* Left Content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div 
              className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] rounded-full mb-5 shadow-lg shadow-[#D4AF37]/20"
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
            >
              <span className="text-label text-[#0A1628]">Our Strategy</span>
            </motion.div>
            
            <motion.h2 
              className="heading-h2 mb-5"
              variants={itemVariants}
            >
              Distressed Cycle
              <br />
              <span className="gradient-text">Specialists</span>
            </motion.h2>
            
            <motion.p 
              className="text-body"
              variants={itemVariants}
            >
              As a Houston private equity real estate firm with 18+ years of market expertise, our team excels at 
              identifying and acquiring distressed properties across Greater Houston. We 
              create value through disciplined execution of our proven 
              repositioning playbook for institutional real estate investments.
            </motion.p>
          </motion.div>

          {/* Right - Yogesh Patil Photo */}
          <motion.div 
            className="flex justify-center lg:justify-end"
            initial={{ opacity: 0, x: 50, scale: 0.95 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="relative">
              {/* Animated Gold Border Frame */}
              <motion.div 
                className="absolute inset-0 border-2 border-[#D4AF37] rounded-2xl"
                style={{ transform: 'translate(16px, 16px)' }}
                animate={{ 
                  boxShadow: ['0 0 20px rgba(212,175,55,0.2)', '0 0 40px rgba(212,175,55,0.4)', '0 0 20px rgba(212,175,55,0.2)']
                }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              
              {/* Photo Container */}
              <motion.div 
                className="relative card-modern rounded-2xl overflow-hidden border-2 border-[#D4AF37]/50 max-w-sm"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_tx-property-experts/artifacts/mxxpj177_WhatsApp%20Image%202026-01-17%20at%2012.50.46%20AM%20%281%29.jpeg"
                  alt="Yogesh Patil - Managing Partner & Founder"
                  className="w-full h-auto object-cover"
                />
                
                {/* Name Overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/90 to-transparent p-6">
                  <h3 className="text-white text-xl font-bold">Yogesh Patil</h3>
                  <p className="gradient-text font-medium">Managing Partner & Founder</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Value Creation Cards - 4 Column Grid */}
        <motion.div 
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={containerVariants}
        >
          {strategies.map((strategy, index) => (
            <motion.div
              key={index}
              className="card-modern rounded-2xl p-6 text-center group"
              variants={itemVariants}
              whileHover={{ y: -10, boxShadow: '0 20px 40px rgba(0,0,0,0.3), 0 0 30px rgba(212,175,55,0.1)' }}
            >
              <motion.div 
                className="w-14 h-14 bg-[#1a2940] rounded-2xl flex items-center justify-center mx-auto mb-4 border border-[#D4AF37]/20"
                whileHover={{ scale: 1.1, rotate: 5, borderColor: 'rgba(212,175,55,0.5)' }}
                transition={{ duration: 0.2 }}
              >
                {strategy.icon}
              </motion.div>
              <h4 className="text-white font-semibold text-lg mb-2">{strategy.title}</h4>
              <p className="text-white/50 text-sm leading-relaxed">
                {strategy.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default StrategySection;
